import React from 'react';
import { DossierTransfert } from './utils/types';
import { formatDate, openEmailClient, openWhatsApp } from './utils/helpers';
import { canSeeArchiveStatus, canSeeRejectedStatus, canModifyStatus, canDeleteSubfoldersAndFiles } from './utils/permissions';

interface TransfertsTableProps {
  transferts: DossierTransfert[];
  currentUserRole: string;
  onOpenTransfert: (transfert: DossierTransfert) => void;
  onChangeStatut: (reference: string, newStatut: any) => void;
  onDelete: (transfert: DossierTransfert) => void;
}

const TransfertsTable: React.FC<TransfertsTableProps> = ({
  transferts,
  currentUserRole,
  onOpenTransfert,
  onChangeStatut,
  onDelete
}) => {
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border-2 sm:border-4 border-blue-100 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b-2 sm:border-b-4 border-blue-200 bg-gradient-to-r from-blue-600 to-blue-700">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center">
          <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Dossiers de transferts ({transferts.length})
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
            <tr>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Contact</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entreprise</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type Transfert</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date dépôt</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {transferts.map((transfert) => (
              <tr key={transfert.reference} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-white transition-all duration-300">
                <TransfertReferenceColumn transfert={transfert} />
                <TransfertClientColumn transfert={transfert} />
                <TransfertContactColumn transfert={transfert} />
                <TransfertEntrepriseColumn transfert={transfert} />
                <TransfertTypeColumn transfert={transfert} />
                <TransfertDateColumn transfert={transfert} />
                <TransfertStatusColumn 
                  transfert={transfert}
                  currentUserRole={currentUserRole}
                  onChangeStatut={onChangeStatut}
                />
                <TransfertActionsColumn 
                  transfert={transfert}
                  currentUserRole={currentUserRole}
                  onOpenTransfert={onOpenTransfert}
                  onDelete={onDelete}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TransfertReferenceColumn: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
    <div className="flex items-center">
      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-bold text-blue-700 truncate max-w-[100px] sm:max-w-none">{transfert.reference}</p>
        <p className="text-xs text-gray-500">{transfert.fichiers.length} fichier(s)</p>
      </div>
    </div>
  </td>
);

const TransfertClientColumn: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
    <div>
      <p className="text-sm font-medium text-gray-900 truncate max-w-[100px] sm:max-w-none">{transfert.nom} {transfert.prenom}</p>
      <p className="text-xs text-gray-500 truncate max-w-[100px] sm:max-w-none">{transfert.profession}</p>
      <p className="text-xs text-gray-400">{transfert.sexe === 'H' ? 'Homme' : 'Femme'}</p>
    </div>
  </td>
);

const TransfertContactColumn: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
    <div className="flex flex-col space-y-1">
      <button
        onClick={() => openEmailClient(transfert.email)}
        className="text-blue-600 hover:text-blue-800 hover:underline text-sm flex items-center"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span className="truncate max-w-[120px]">{transfert.email}</span>
      </button>
      {transfert.telephone && (
        <button
          onClick={() => openWhatsApp(transfert.telephone || '', transfert.nom, transfert.prenom, 'transfert', transfert.reference, 'votre dossier de transfert')}
          className="text-green-600 hover:text-green-800 text-xs flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="truncate max-w-[100px]">{transfert.telephone}</span>
        </button>
      )}
    </div>
  </td>
);

const TransfertEntrepriseColumn: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <td className="px-4 sm:px-6 py-4">
    <p className="text-sm text-gray-900 truncate max-w-[120px]" title={transfert.entreprise || 'Pas défini'}>
      {transfert.entreprise || 'Pas défini'}
    </p>
  </td>
);

const TransfertTypeColumn: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <td className="px-4 sm:px-6 py-4">
    <p className="text-sm text-gray-900 truncate max-w-[150px]" title={transfert.typeTransfert}>
      {transfert.typeTransfert}
    </p>
  </td>
);

const TransfertDateColumn: React.FC<{ transfert: DossierTransfert }> = ({ transfert }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
    {formatDate(transfert.dateCreation)}
  </td>
);

const TransfertStatusColumn: React.FC<{ 
  transfert: DossierTransfert;
  currentUserRole: string;
  onChangeStatut: (reference: string, newStatut: any) => void;
}> = ({ transfert, currentUserRole, onChangeStatut }) => {
  const getStatusClasses = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'bg-yellow-100 text-yellow-800';
      case 'partiellement_apuré': return 'bg-orange-100 text-orange-800';
      case 'apuré': return 'bg-green-100 text-green-800';
      case 'archivé': return 'bg-blue-100 text-blue-800';
      case 'rejeté': return 'bg-red-600 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
      <select
        value={transfert.statut}
        onChange={(e) => onChangeStatut(transfert.reference, e.target.value as any)}
        disabled={!canModifyStatus(currentUserRole)}
        className={`text-xs px-2 sm:px-3 py-1 rounded-full border-0 font-medium focus:ring-2 transition-all cursor-pointer ${getStatusClasses(transfert.statut)}`}
      >
        <option value="en_attente">En attente</option>
        <option value="partiellement_apuré">Partiellement apuré</option>
        <option value="apuré">Apuré</option>
        {canSeeArchiveStatus(currentUserRole) && <option value="archivé">Archivé</option>}
        {canSeeRejectedStatus(currentUserRole) && <option value="rejeté">Rejeté</option>}
      </select>
    </td>
  );
};

const TransfertActionsColumn: React.FC<{ 
  transfert: DossierTransfert;
  currentUserRole: string;
  onOpenTransfert: (transfert: DossierTransfert) => void;
  onDelete: (transfert: DossierTransfert) => void;
}> = ({ transfert, currentUserRole, onOpenTransfert, onDelete }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
    <div className="flex justify-end space-x-1 sm:space-x-2 flex-wrap gap-1">
      <button
        onClick={() => onOpenTransfert(transfert)}
        className="text-blue-600 hover:text-purple-800 bg-blue-50 hover:bg-blue-100 px-2 sm:px-3 py-1 rounded-lg transition-all duration-300 text-xs sm:text-sm"
      >
        Voir
      </button>
      {(currentUserRole === 'admin_bank' || currentUserRole === 'superviseur') && transfert.statut !== 'rejeté' && (
        <button
          onClick={() => onDelete(transfert)}
          className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1 rounded-lg transition-all duration-300 text-xs sm:text-sm"
        >
          Supprimer
        </button>
      )}
    </div>
  </td>
);

export default TransfertsTable;
